import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { RundownItem } from "../types";

interface MirrorColumnProps {
  collapsed?: boolean;
  onToggle?: () => void;
  noToggle?: boolean;
  items?: RundownItem[];
  onSelect?: (id: string) => void;
  serverTime?: string;
}

export const MirrorColumn: React.FC<MirrorColumnProps> = ({
  collapsed = false,
  onToggle,
  noToggle = false,
  items = [],
  onSelect,
  serverTime = "--:--:--",
}) => {
  const { t } = useTranslation();
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  if (collapsed) {
    return (
      <div
        data-testid="mirror-column-collapsed"
        style={{
          height: "100%",
          background: "var(--color-panel-bg)",
          borderRadius: "var(--border-radius-md)",
          boxShadow: "var(--shadow-card)",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexDirection: "column",
          border: "1px solid var(--color-panel-border)",
          paddingTop: "20px",
          cursor: "pointer",
          transition: "all 0.3s ease",
        }}
        onClick={onToggle}
      >
        <span style={{ color: "var(--color-text-muted)", marginBottom: "20px" }}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </span>
        <div
          style={{
            writingMode: "vertical-rl",
            textOrientation: "mixed",
            color: "var(--color-text-secondary)",
            fontWeight: "bold",
            letterSpacing: "2px",
          }}
        >
          {t("logger.mirror.rundown")}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="mirror-column"
      style={{
        height: "100%",
        background: "var(--color-panel-bg)",
        borderRadius: "var(--border-radius-md)",
        boxShadow: "var(--shadow-card)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: "1px solid var(--color-panel-border)",
        position: "relative",
      }}
    >
      {/* Collapse Button */}
      {!noToggle && (
        <Button
          data-testid="mirror-column-toggle"
          variant="ghost"
          size="icon-sm"
          className="absolute top-3 left-3 z-10 text-muted-foreground hover:text-foreground"
          onClick={onToggle}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </Button>
      )}

      <div
        data-testid="mirror-column-header"
        style={{
          padding: "12px 20px",
          borderBottom: "1px solid var(--color-panel-border)",
          background: "var(--color-bg-base)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          justifyContent: "flex-start",
        }}
      >
        <i
          className="bx bx-list-ul"
          style={{ fontSize: "var(--text-lg)", color: "var(--color-data-rundown)" }}
        ></i>
        <span
          style={{
            fontWeight: "900",
            letterSpacing: "2px",
            fontSize: "var(--text-md)",
            color: "#ffffff",
          }}
        >
          {t("logger.mirror.rundown")}
        </span>
      </div>

      <div
        data-testid="mirror-column-items"
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          padding: "10px 10px 24px 10px",
        }}
      >
        {items.map((item, idx) => {
          const isLive = item.status === "live";
          const showParallel = item.isParallel || items[idx + 1]?.isParallel;

          return (
            <div
              key={item.id}
              data-testid={`mirror-rundown-item-${idx}`}
              onClick={() => onSelect && onSelect(item.id)}
              onMouseEnter={() => setHoveredItemId(item.id)}
              onMouseLeave={() => setHoveredItemId(null)}
              style={{
                padding: "12px 15px",
                borderBottom: "1px solid var(--color-panel-border)",
                background:
                  item.status === "live" ? "rgba(56, 189, 248, 0.1)" : "var(--color-bg-base)",
                display: "grid",
                gridTemplateColumns: "min-content 1fr", // No Action Column in Logger View
                gap: "12px",
                alignItems: "center",
                // REMOVED opacity on container to allow popup to be fully opaque
                marginBottom: "8px",
                borderRadius: "var(--border-radius-md)",
                border:
                  item.status === "live"
                    ? "1px solid var(--color-data-event)"
                    : "1px solid var(--color-panel-border)",
                cursor: "pointer",
                position: "relative", // Needed for popup
                zIndex: hoveredItemId === item.id ? 100 : 1, // Ensure hovered item is on top
              }}
            >
              {/* Badge */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  opacity: item.status === "past" ? 0.5 : 1,
                }}
              >
                <span
                  style={{
                    color:
                      item.status === "live"
                        ? "var(--color-data-event)"
                        : "var(--color-text-muted)",
                    fontSize: "var(--text-md)",
                    fontWeight: "900",
                    whiteSpace: "nowrap",
                  }}
                >
                  #{idx + 1}
                </span>
              </div>

              {/* Title */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  overflow: "hidden",
                  opacity: item.status === "past" ? 0.5 : 1,
                }}
              >
                {showParallel && (
                  <span
                    style={{
                      color: "var(--color-accent-primary)",
                      fontWeight: "bold",
                      fontSize: "var(--text-lg)",
                      lineHeight: 1,
                    }}
                  >
                    ║
                  </span>
                )}
                <span
                  style={{
                    fontSize: "var(--text-md)",
                    fontWeight: "bold",
                    color: item.status === "live" ? "#fff" : "var(--color-text-primary)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.title}
                </span>
              </div>

              {/* Hover Popup */}
              {hoveredItemId === item.id && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "10px",
                    zIndex: 9999,
                    background: "var(--color-bg-base)", // Dark Blue
                    border: "1px solid var(--color-data-event)",
                    borderRadius: "var(--border-radius-md)",
                    padding: "16px",
                    color: "var(--color-text-primary)",
                    width: "fit-content",
                    maxWidth: "400px", // Slightly smaller for side column
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.8)",
                    marginTop: "4px",
                    fontStyle: "normal",
                    fontSize: "var(--text-base)",
                    lineHeight: "1.4",
                    pointerEvents: "none",
                    minWidth: "200px",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(255, 255, 255, 0.8)", // WHITE Title
                      fontWeight: "bold",
                      fontSize: "var(--text-md)",
                      marginBottom: "6px",
                      borderBottom: "1px solid var(--color-panel-border)",
                      paddingBottom: "4px",
                    }}
                  >
                    {item.title}
                  </div>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--color-data-event)",
                      marginBottom: "2px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                    }}
                  >
                    {t("logger.mirror.description")}
                  </div>
                  <div>{item.description || "-"}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
